import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { useLang } from "../../lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SHOOT_IDS = ["spring-summer", "autumn-winter", "graduation", "schools", "other"];

const TERRITORIES = [
  { id: "uk", flag: "🇬🇧", labelKey: "UK" },
  { id: "uae", flag: "🇦🇪", labelKey: "UAE" },
];

export default function BookingDialog({ open, onOpenChange, mode = "book" }) {
  const { t } = useLang();
  const isOrder = mode === "order";
  const [form, setForm] = useState({
    name: "",
    organisation: "",
    email: "",
    phone: "",
    territory: "",
    shoot_type: isOrder ? "Order Photos" : "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.organisation || !form.email || !form.phone || !form.territory) {
      toast.error(t("booking.validation"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, form);
      toast.success(isOrder ? t("booking.successOrder") : t("booking.success"));
      setForm({
        name: "",
        organisation: "",
        email: "",
        phone: "",
        territory: "",
        shoot_type: isOrder ? "Order Photos" : "",
        message: "",
      });
      onOpenChange(false);
    } catch (err) {
      toast.error(t("booking.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="booking-dialog"
        className="max-w-xl bg-white border-2 border-black rounded-none p-8 sm:p-12"
      >
        <DialogHeader>
          <DialogTitle dir="auto" className="font-display font-black uppercase text-3xl sm:text-4xl tracking-tight leading-none">
            {isOrder ? t("booking.orderTitle") : t("booking.bookTitle")}
          </DialogTitle>
        </DialogHeader>

        <form
          onSubmit={submit}
          data-testid="booking-form"
          className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5"
        >
          <Field id="booking-name" label={t("booking.name")} value={form.name} onChange={onChange("name")} required />
          <Field id="booking-org" label={t("booking.org")} value={form.organisation} onChange={onChange("organisation")} required />
          <Field id="booking-email" type="email" label={t("booking.email")} value={form.email} onChange={onChange("email")} required />
          <Field id="booking-phone" label={t("booking.phone")} value={form.phone} onChange={onChange("phone")} required />

          {/* Territory selector — required, styled like shoot-type tabs */}
          <div className="sm:col-span-2">
            <label dir="auto" className="block font-body text-[10px] uppercase tracking-[0.25em] font-bold text-black/60 mb-2">
              {t("booking.territory")}<span className="text-black ml-1">*</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {TERRITORIES.map((tr) => (
                <button
                  type="button"
                  key={tr.id}
                  data-testid={`booking-territory-${tr.id}`}
                  onClick={() => setForm({ ...form, territory: tr.labelKey })}
                  dir="auto"
                  className={`px-4 py-2 border text-[10px] uppercase tracking-[0.2em] font-bold transition-colors inline-flex items-center gap-2 ${
                    form.territory === tr.labelKey
                      ? "bg-black text-white border-black"
                      : "bg-white text-black border-black/30 hover:border-black"
                  }`}
                >
                  <span aria-hidden className="text-base leading-none">{tr.flag}</span>
                  {tr.labelKey}
                </button>
              ))}
            </div>
          </div>

          {!isOrder && (
            <div className="sm:col-span-2">
              <label dir="auto" className="block font-body text-[10px] uppercase tracking-[0.25em] font-bold text-black/60 mb-2">
                {t("booking.shootType")}
              </label>
              <div className="flex flex-wrap gap-2">
                {SHOOT_IDS.map((id) => (
                  <button
                    type="button"
                    key={id}
                    data-testid={`booking-shoot-${id}`}
                    onClick={() => setForm({ ...form, shoot_type: t(`booking.shootTypes.${id}`) })}
                    dir="auto"
                    className={`px-4 py-2 border text-[10px] uppercase tracking-[0.2em] font-bold transition-colors ${
                      form.shoot_type === t(`booking.shootTypes.${id}`)
                        ? "bg-black text-white border-black"
                        : "bg-white text-black border-black/30 hover:border-black"
                    }`}
                  >
                    {t(`booking.shootTypes.${id}`)}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="sm:col-span-2">
            <label dir="auto" className="block font-body text-[10px] uppercase tracking-[0.25em] font-bold text-black/60 mb-2">
              {t("booking.message")}
            </label>
            <textarea
              data-testid="booking-message"
              rows={3}
              value={form.message}
              onChange={onChange("message")}
              placeholder={isOrder ? t("booking.messagePlaceholderOrder") : t("booking.messagePlaceholderBook")}
              dir="auto"
              className="w-full bg-transparent border-0 border-b border-black/25 focus:border-black focus:outline-none focus:ring-0 py-2 text-base font-body text-black placeholder:text-black/30 resize-none"
            />
          </div>

          <div className="sm:col-span-2 mt-2 flex items-center gap-4">
            <button
              type="submit"
              disabled={loading}
              data-testid="booking-submit-btn"
              dir="auto"
              className="inline-flex items-center gap-2 bg-black text-white px-7 py-4 text-[11px] uppercase tracking-[0.25em] font-bold border border-black hover:bg-white hover:text-black transition-colors disabled:opacity-60"
            >
              {loading ? t("booking.sending") : t("booking.send")}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ id, label, value, onChange, type = "text", required }) {
  return (
    <div>
      <label
        htmlFor={id}
        dir="auto"
        className="block font-body text-[10px] uppercase tracking-[0.25em] font-bold text-black/60 mb-2"
      >
        {label}
        {required && <span className="text-black ml-1">*</span>}
      </label>
      <input
        id={id}
        data-testid={id}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        dir="auto"
        className="w-full bg-transparent border-0 border-b border-black/25 focus:border-black focus:outline-none focus:ring-0 py-2 text-base font-body text-black placeholder:text-black/30"
      />
    </div>
  );
}
